<template>
  <div class="card card-custom card-shadowless border posting-rail mb-5">
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span class="card-label font-weight-bolder text-dark">
          <i class="fa fa-tasks mr-2 text-primary"></i>
          Post to INSIGHT — steps
        </span>
      </div>
      <div class="card-toolbar">
        <button
          type="button"
          class="btn btn-sm btn-light-primary font-weight-bold"
          :disabled="busy"
          @click.prevent="refreshStatus"
        >
          <i class="fa fa-sync" :class="{ 'fa-spin': statusLoading }"></i>
          Refresh status
        </button>
      </div>
    </div>

    <div class="card-body pt-2 pb-5">
      <p class="text-muted font-size-sm mb-4">
        Stay on this screen. Create the draft, attach the schedule, then post.
        Do not post from the INSIGHT UI.
      </p>

      <ol class="posting-rail__steps mb-0 pl-4">
        <li class="mb-4" :class="stepClass('draft')">
          <div class="font-weight-bold text-dark mb-1">Create draft in INSIGHT</div>
          <div class="font-size-sm text-muted mb-2">
            <span v-if="hasDraft">
              Draft ready
              <span v-if="transactionId"> — transaction {{ transactionId }}</span>.
            </span>
            <span v-else-if="createLaneNextStep">
              {{ createLaneNextStep }}
            </span>
            <span v-else>
              Creates (or adopts) the unposted INSIGHT transaction for this billing line.
            </span>
          </div>
          <button
            v-if="!hasDraft && !isPosted && !createLaneNextStep"
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="!canCreateDraft || busy"
            @click.prevent="onCreateDraft"
          >
            <i class="fa fa-file-medical" :class="{ 'fa-spin': creating }"></i>
            Create draft
          </button>
          <div v-else-if="hasDraft" class="text-success font-size-sm">
            <i class="fa fa-check-circle"></i> Done
          </div>
          <div v-if="showRecoveryField" class="mt-3">
            <label class="font-weight-bold font-size-sm">
              {{ recoveryFieldLabel }}
            </label>
            <p v-if="recoverableAssemblyRefusal" class="font-size-sm text-muted mb-2">
              {{ recoveryFieldHelp }}
            </p>
            <input
              v-model="recoveryAcknowledgement"
              type="text"
              class="form-control form-control-sm"
              placeholder="e.g. POL-11312:50:87:61:91"
              autocomplete="off"
            />
          </div>
        </li>

        <li class="mb-4" :class="stepClass('schedule')">
          <div class="font-weight-bold text-dark mb-1">Attach schedule (.docx)</div>
          <div class="font-size-sm text-muted mb-2">
            <span v-if="scheduleAttached && !reuploadScheduleOpen">
              Schedule is attached to the draft. Re-upload if you need a corrected Word file before posting.
            </span>
            <span v-else-if="scheduleAttached && reuploadScheduleOpen">
              Re-uploading replaces the schedule on this draft. Finish attach before you Post.
            </span>
            <span v-else>
              Generate the policy schedule from source, or attach the signed Word document directly.
            </span>
          </div>

          <div
            v-if="hasDraft && scheduleAttached && !isPosted && !reuploadScheduleOpen"
            class="d-flex flex-wrap align-items-center"
          >
            <div class="text-success font-size-sm mr-3 mb-2">
              <i class="fa fa-check-circle"></i> Done
            </div>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold mb-2"
              :disabled="busy"
              @click.prevent="openReuploadSchedule"
            >
              <i class="fa fa-upload"></i>
              Re-upload schedule to draft
            </button>
          </div>

          <div v-if="hasDraft && !isPosted && (!scheduleAttached || reuploadScheduleOpen)">
            <div
              v-if="reuploadScheduleOpen"
              class="d-flex flex-wrap align-items-center mb-2"
            >
              <button
                type="button"
                class="btn btn-sm btn-light font-weight-bold mb-2"
                :disabled="busy"
                @click.prevent="cancelReuploadSchedule"
              >
                Cancel re-upload
              </button>
            </div>
            <!--begin: Generate schedule (Desk) -->
            <div class="d-flex flex-wrap align-items-center mb-2">
              <button
                type="button"
                class="btn btn-primary font-weight-bold mr-3 mb-2"
                :disabled="busy"
                @click.prevent="onGenerateSchedule"
              >
                <i class="fa fa-file-word" :class="{ 'fa-spin': generating }"></i>
                {{ scheduleAttached ? "Generate replacement schedule" : "Generate schedule" }}
              </button>
            </div>

            <div
              v-if="scheduleRefusals.length"
              class="mb-3 font-size-sm"
              :class="
                scheduleGenerationConfigError
                  ? 'alert alert-custom alert-light-secondary py-2 px-3'
                  : 'alert alert-custom alert-light-warning py-2 px-3'
              "
            >
              <div class="font-weight-bold text-dark mb-1">
                <span v-if="scheduleGenerationConfigError">Schedule generation isn't available</span>
                <span v-else>Desk couldn't assemble the schedule</span>
              </div>
              <ul class="mb-0 pl-4">
                <li v-for="(line, index) in scheduleRefusals" :key="index">{{ line }}</li>
              </ul>
            </div>

            <!--begin: Preview + confirm gate -->
            <div v-if="scheduleGeneration" class="border rounded p-3 mb-3">
              <div class="font-weight-bold text-dark mb-2">Schedule ready — review before attach</div>

              <div v-if="scheduleGeneration.pdfBase64" class="mb-3">
                <embed
                  :src="pdfPreviewSrc"
                  type="application/pdf"
                  style="width: 100%; height: 480px; border: 1px solid #e4e6ef;"
                />
              </div>
              <div v-else class="mb-3 p-3 bg-light rounded">
                <div class="font-weight-bold text-dark mb-2">
                  <i class="fa fa-file-word text-primary"></i>
                  {{ scheduleDocxFileName }}
                </div>
                <div class="font-size-sm text-muted mb-3">
                  Word schedule generated
                  <span v-if="scheduleDocxSizeLabel"> ({{ scheduleDocxSizeLabel }})</span>.
                  PDF on-screen preview is not available yet — download the DOCX to review it.
                </div>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  :disabled="!scheduleGeneration.renderToken && !scheduleGeneration.contentBase64"
                  @click.prevent="downloadPendingDocx"
                >
                  <i class="fa fa-download"></i>
                  Download schedule
                </button>
              </div>

              <table class="table table-sm table-borderless mb-2 font-size-sm">
                <tbody>
                  <tr>
                    <td class="text-muted">Total premium</td>
                    <td class="font-weight-bold text-dark">
                      {{ displayMoney(schedulePreview.totalPremium) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="text-muted">Vehicles</td>
                    <td class="font-weight-bold text-dark">
                      {{ displayValue(schedulePreview.vehicleCount) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="text-muted">Schedule face</td>
                    <td class="font-weight-bold text-dark">
                      <span v-if="schedulePreviewTermBlocks.length">
                        {{ schedulePreviewTermBlocks.join(" · ") }}
                      </span>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-muted">Snapshot hash</td>
                    <td class="font-weight-bold text-dark">
                      <code>{{ displayValue(schedulePreview.snapshotHash) }}</code>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-muted">Document hash</td>
                    <td class="font-weight-bold text-dark">
                      <code>{{ displayValue(scheduleDocxHashShort) }}</code>
                    </td>
                  </tr>
                  <tr v-if="scheduleGeneration.generatedAt">
                    <td class="text-muted">Generated</td>
                    <td class="font-weight-bold text-dark">
                      {{ scheduleGeneration.generatedAt }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mb-3">
                <label class="font-weight-bold font-size-sm">
                  Type the total premium shown above to confirm
                </label>
                <input
                  v-model="confirmTypedPremium"
                  type="text"
                  class="form-control form-control-sm"
                  style="max-width: 220px;"
                  placeholder="e.g. 1234.56"
                  autocomplete="off"
                  :disabled="busy"
                />
              </div>

              <button
                type="button"
                class="btn btn-success font-weight-bold"
                :disabled="busy || !confirmTypedPremiumMatches"
                @click.prevent="onAttachGeneratedSchedule"
              >
                <i class="fa fa-paperclip" :class="{ 'fa-spin': attaching }"></i>
                {{ scheduleAttached ? "Re-upload to draft" : "Attach to INSIGHT" }}
              </button>
            </div>
            <!--end: Preview + confirm gate -->

            <!--begin: Manual fallback (original file-upload path, unchanged) -->
            <div class="mt-2">
              <a href="#" class="font-size-sm" @click.prevent="showManualUpload = !showManualUpload">
                {{
                  showManualUpload
                    ? "Hide manual upload"
                    : scheduleAttached
                      ? "Or upload a schedule file directly"
                      : "Or attach a schedule file directly"
                }}
              </a>
              <div v-if="showManualUpload" class="d-flex flex-wrap align-items-center mt-2">
                <input
                  ref="scheduleInput"
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  class="form-control-file mr-3 mb-2"
                  :disabled="busy"
                  @change="onScheduleFile"
                />
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold mb-2"
                  :disabled="busy || !pendingScheduleBase64"
                  @click.prevent="onAttachSchedule"
                >
                  <i class="fa fa-paperclip" :class="{ 'fa-spin': attaching }"></i>
                  {{ scheduleAttached ? "Re-upload schedule" : "Attach schedule" }}
                </button>
              </div>
            </div>
            <!--end: Manual fallback -->
          </div>
          <div v-else-if="!hasDraft" class="font-size-sm text-muted">
            Create the draft first.
          </div>
        </li>

        <li :class="stepClass('post')">
          <div class="font-weight-bold text-dark mb-1">Post the transaction</div>
          <div class="font-size-sm text-muted mb-2">
            <span v-if="isPosted">Posted.</span>
            <span v-else-if="showPostReadinessWait" class="text-info">
              <i class="fa fa-spinner fa-spin mr-1"></i>
              Checking post readiness… {{ readinessElapsedSec }}s
            </span>
            <span v-else-if="reuploadScheduleOpen">
              Finish the schedule re-upload before posting.
            </span>
            <span v-else-if="scheduleAttached">
              Checks are clear — use Post below. Type the policy number to confirm.
            </span>
            <span v-else>Attach the schedule before posting unlocks.</span>
          </div>
        </li>
      </ol>

      <b-alert v-if="railError" variant="warning" show class="mt-4 mb-0">
        <div class="text-dark">{{ railError }}</div>
        <div v-if="railInsightErrors.length" class="mt-2">
          <div class="font-weight-bold text-dark font-size-sm">INSIGHT said</div>
          <div
            v-for="(line, index) in railInsightErrors"
            :key="index"
            class="font-size-sm text-dark"
          >
            {{ line }}
          </div>
        </div>
        <button
          v-if="canCompare && !comparison"
          type="button"
          class="btn btn-sm btn-light-primary font-weight-bold mt-3"
          :disabled="busy"
          @click.prevent="loadComparison"
        >
          <i class="fa fa-not-equal" :class="{ 'fa-spin': comparing }"></i>
          Compare with INSIGHT
        </button>
      </b-alert>
      <b-alert v-else-if="railInfo" variant="success" show class="mt-4 mb-0">
        <div class="text-dark">{{ railInfo }}</div>
      </b-alert>

      <div v-if="comparison" class="mt-4 border rounded p-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div class="font-weight-bolder text-dark">
            INSIGHT transaction {{ comparison.transactionId }} vs the reviewed draft
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light font-weight-bold"
            :disabled="busy"
            @click.prevent="loadComparison"
          >
            <i class="fa fa-sync" :class="{ 'fa-spin': comparing }"></i>
            Re-check
          </button>
        </div>

        <div v-if="comparison.matches" class="text-success font-size-sm mb-3">
          <i class="fa fa-check-circle"></i>
          Every field now matches — use Create draft to adopt it.
        </div>

        <table v-if="mismatchedRows.length" class="table table-sm table-borderless mb-3">
          <thead>
            <tr class="text-muted font-size-sm">
              <th>Field</th>
              <th>Reviewed draft</th>
              <th>INSIGHT now</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in mismatchedRows" :key="row.field">
              <td class="font-weight-bold text-dark">
                {{ row.field }}
                <span v-if="row.actualSourceKey" class="text-muted font-weight-normal">
                  (read from {{ row.actualSourceKey }})
                </span>
              </td>
              <td>{{ displayValue(row.expected) }}</td>
              <td class="text-danger">{{ displayValue(row.actual) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="arAllocation" class="font-size-sm mb-3">
          <div class="font-weight-bold text-dark mb-1">
            Authorised rep allocation — this is what INSIGHT works the invoice debtor out from
          </div>
          <div
            class="mb-2"
            :class="arAllocation.matches ? 'text-success' : 'text-danger'"
          >
            <i
              class="fa"
              :class="arAllocation.matches ? 'fa-check-circle' : 'fa-times-circle'"
            ></i>
            {{ arAllocation.summary }}
          </div>
          <table class="table table-sm table-borderless mb-2">
            <thead>
              <tr class="text-muted font-size-sm">
                <th>Position</th>
                <th>Reviewed draft</th>
                <th>INSIGHT now</th>
                <th>Commission</th>
                <th>Fee</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in arAllocation.rows" :key="'ar' + row.position">
                <td class="font-weight-bold text-dark">{{ row.label }}</td>
                <td>{{ displayValue(row.expectedAuthorisedRepId) }}</td>
                <td :class="row.identityMatches ? '' : 'text-danger'">
                  {{ displayValue(row.actualAuthorisedRepId) }}
                </td>
                <td :class="row.commissionMatches ? '' : 'text-danger'">
                  {{ displayValue(row.expectedCommission) }}
                  <span v-if="!row.commissionMatches">
                    → {{ displayValue(row.actualCommission) }}
                  </span>
                </td>
                <td :class="row.feeMatches ? '' : 'text-danger'">
                  {{ displayValue(row.expectedFee) }}
                  <span v-if="!row.feeMatches">
                    → {{ displayValue(row.actualFee) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="text-muted">{{ arAllocation.positionNote }}</div>
        </div>

        <div v-if="comparison.debtor" class="font-size-sm text-muted mb-3">
          <div class="font-weight-bold text-dark mb-1">
            Invoice debtor — informational, INSIGHT derives it when the transaction is posted
          </div>
          <div>
            INSIGHT currently holds {{ displayValue(comparison.debtor.actual) }}
            <span v-if="comparison.debtorSourceKey">
              (read from {{ comparison.debtorSourceKey }})
            </span>
            <span v-if="!comparison.isPosted">
              — expected for an unposted transaction, which carries the insured until it is posted.
            </span>
          </div>
          <div>
            After posting this should become
            {{ displayValue(comparison.expectedDebtorAfterPost) }}, the producing broker on AR1.
            The post read-back checks that and reports a discrepancy if it does not.
          </div>
          <div v-if="debtorCandidateRows.length" class="mt-1">
            <span
              v-for="candidate in debtorCandidateRows"
              :key="candidate.key"
              class="mr-3"
            >
              {{ candidate.key }}: {{ displayValue(candidate.value) }}
            </span>
          </div>
        </div>

        <div v-if="comparison.echo" class="font-size-sm text-muted mb-3">
          <div class="font-weight-bold text-dark mb-1">
            Whether INSIGHT will accept the reviewed figures (check only — nothing is written)
          </div>
          <div>
            Check result: {{ comparison.echo.isValid ? "accepted" : "not accepted" }}
            <span v-if="comparison.echo.status">(reference {{ comparison.echo.status }})</span>
          </div>
          <div v-for="(line, index) in comparison.echo.errors" :key="'echo' + index">
            {{ line }}
          </div>
        </div>

        <div class="border-top pt-3 mb-3">
          <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
            <div class="font-weight-bold text-dark">
              Debtor checks — read-only (nothing is written)
            </div>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              :disabled="busy"
              @click.prevent="loadDebtorProbe"
            >
              <i class="fa fa-vial" :class="{ 'fa-spin': probing }"></i>
              {{ debtorProbeResult ? "Re-run checks" : "Run debtor checks" }}
            </button>
          </div>
          <div class="font-size-sm text-muted mb-2">
            Background only — we do not send the debtor. INSIGHT works it out when the transaction
            is posted, from the authorised-rep allocation above. These checks read the parties
            involved (can this party be billed?), show what a posted debtor looks like on a similar
            case, and confirm nothing is written while you review.
          </div>

          <div v-if="debtorProbeError" class="text-danger font-size-sm">
            {{ debtorProbeError }}
          </div>

          <template v-if="debtorProbeResult">
            <div class="font-size-sm text-dark mb-2">
              Reviewed debtor {{ displayValue(debtorProbeResult.targetDebtorId) }} — INSIGHT holds
              {{ displayValue(debtorProbeResult.insightDebtorNow) }}
              <span v-if="debtorProbeResult.insightDebtorNowKey">
                (read from {{ debtorProbeResult.insightDebtorNowKey }})
              </span>
            </div>

            <!--begin: Entity probes (the decisive half) -->
            <div v-if="debtorProbeResult.entityProbes" class="mb-3">
              <div class="font-weight-bold text-dark font-size-sm mb-1">
                Can these parties be billed? (read-only entity reads)
              </div>
              <div class="text-muted font-size-sm mb-1">
                INSIGHT shares one entity id space across clients, insurers and authorised reps, so a
                successful read is not proof of a role — the Roles bitmask and IsClient are.
              </div>
              <table class="table table-sm table-borderless mb-1">
                <thead>
                  <tr class="text-muted font-size-sm">
                    <th>Entity</th>
                    <th>Client role (IsClient)</th>
                    <th>Usual Debtor</th>
                    <th>Roles bitmask</th>
                    <th>Fields INSIGHT returned</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="entity in debtorProbeResult.entityProbes"
                    :key="'entity' + entity.entityId + entity.label"
                  >
                    <td class="font-weight-bold text-dark">
                      {{ entity.entityId }}
                      <div class="text-muted font-weight-normal font-size-sm">
                        {{ entity.label }}
                      </div>
                    </td>
                    <td :class="entity.isClientRole ? 'text-success' : 'text-danger'">
                      {{ entity.isClientRole ? "yes" : "no" }}
                      <div class="text-muted font-size-sm">
                        client record readable: {{ entity.clientReadOk ? "yes" : "no" }}
                      </div>
                      <div v-if="entity.clientError" class="text-danger font-size-sm">
                        {{ entity.clientError }}
                      </div>
                    </td>
                    <td class="font-size-sm">
                      <template v-if="entity.usualDebtor">
                        <span class="text-success font-weight-bold">
                          {{ entity.usualDebtor.id }}
                        </span>
                        <div class="text-muted">
                          {{ entity.usualDebtor.name }}
                          <span v-if="entity.usualDebtor.entityType">
                            ({{ entity.usualDebtor.entityType }})
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <span class="text-muted">none — bills itself</span>
                      </template>
                      <div class="text-muted font-size-sm">
                        UsualDebtorId verbatim:
                        <code>{{ displayValue(entity.usualDebtorIdRaw) }}</code>
                      </div>
                    </td>
                    <td class="font-size-sm">
                      {{ displayValue(entity.roles) }}
                      <div class="text-muted">
                        broker endpoint answered:
                        {{ entity.producingBrokerReadOk ? "yes" : "no" }}
                      </div>
                      <div v-if="entity.producingBrokerError" class="text-muted">
                        {{ entity.producingBrokerError }}
                      </div>
                    </td>
                    <td class="font-size-sm">
                      <div v-if="entity.client">
                        <span class="text-muted">client:</span> {{ asJson(entity.client) }}
                      </div>
                      <div v-if="entity.producingBroker">
                        <span class="text-muted">broker:</span>
                        {{ asJson(entity.producingBroker) }}
                      </div>
                      <span v-if="!entity.client && !entity.producingBroker">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                v-if="debtorProbeResult.entityConclusion"
                class="font-size-sm"
                :class="
                  debtorProbeResult.entityProbes.length &&
                  debtorProbeResult.entityProbes[0].isClientRole
                    ? 'text-dark'
                    : 'text-danger'
                "
              >
                {{ debtorProbeResult.entityConclusion }}
              </div>
              <div
                v-if="debtorProbeResult.clientComparison"
                class="font-size-sm text-dark mt-1"
              >
                {{ debtorProbeResult.clientComparison.verdict }}
              </div>
            </div>
            <!--end: Entity probes -->

            <!--begin: NextActions -->
            <div v-if="debtorProbeResult.nextActions" class="mb-3">
              <div class="font-weight-bold text-dark font-size-sm">
                What INSIGHT will currently accept on transaction
                {{ debtorProbeResult.transactionId }}
              </div>
              <div class="text-muted font-size-sm mb-1">
                INSIGHT's own NextActions block, read before attempting anything.
                A flag shown as absent means INSIGHT did not report it — that is
                not the same as a no.
              </div>
              <template v-if="debtorProbeResult.nextActions.available">
                <span
                  v-for="(value, key) in debtorProbeResult.nextActions.flags"
                  :key="'na' + key"
                  class="label label-inline mr-2 mb-1"
                  :class="value === true ? 'label-light-success' : 'label-light'"
                >
                  {{ key }}: {{ displayValue(value) }}
                </span>
              </template>
              <div v-else class="text-muted font-size-sm">
                {{ debtorProbeResult.nextActions.note }}
              </div>
            </div>
            <!--end: NextActions -->

            <!--begin: Posted exemplar -->
            <div v-if="debtorProbeResult.postedExemplar" class="mb-3">
              <div class="font-weight-bold text-dark font-size-sm mb-1">
                What a debtor looks like on a POSTED transaction (INSIGHT transaction
                {{ debtorProbeResult.postedExemplar.transactionId }})
              </div>
              <div
                v-if="debtorProbeResult.postedExemplar.error"
                class="text-danger font-size-sm"
              >
                {{ debtorProbeResult.postedExemplar.error }}
              </div>
              <div v-else class="font-size-sm text-dark">
                <div class="text-muted mb-1">
                  Posted: {{ debtorProbeResult.postedExemplar.isPosted ? "yes" : "no" }}
                </div>
                <div
                  v-for="(value, key) in debtorProbeResult.postedExemplar.keys"
                  :key="'exemplar' + key"
                >
                  <span class="text-muted">{{ key }}:</span> {{ asJson(value) }}
                </div>
              </div>
            </div>
            <!--end: Posted exemplar -->

            <div class="font-weight-bold text-dark font-size-sm mb-1">
              Confirmation that the debtor is derived, not sent
            </div>
            <div class="text-muted font-size-sm mb-1">
              The vendor Policy Transaction API carries DebtorId in responses
              only — it appears in no request schema on any endpoint. These rows
              are not a search for a request shape that works; there is none.
              They exist to show that INSIGHT normalises the debtor back to the
              policy client no matter where the value is placed.
            </div>
            <div
              v-if="debtorProbeResult.dryRunCaveat"
              class="alert alert-custom alert-light-warning py-2 px-3 mb-2 font-size-sm"
            >
              {{ debtorProbeResult.dryRunCaveat }}
            </div>
            <table class="table table-sm table-borderless mb-2">
              <thead>
                <tr class="text-muted font-size-sm">
                  <th>Where we put the debtor</th>
                  <th>INSIGHT echoed</th>
                  <th>Read from</th>
                  <th>Wizard validator only</th>
                  <th>INSIGHT said</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="variant in debtorProbeResult.variants" :key="variant.label">
                  <td class="font-weight-bold text-dark">
                    {{ variant.label }}
                    <div class="text-muted font-weight-normal font-size-sm">
                      {{ variant.description }}
                    </div>
                  </td>
                  <td :class="variant.keptDebtor ? 'text-success' : 'text-danger'">
                    {{ displayValue(variant.echoedDebtorId) }}
                    <span v-if="variant.keptDebtor">(kept)</span>
                    <span v-else-if="variant.echoedDebtorId === null">
                      (no debtor key returned — not a pass)
                    </span>
                    <span v-else>(not kept)</span>
                  </td>
                  <td class="font-size-sm">{{ displayValue(variant.echoedFrom) }}</td>
                  <td class="font-size-sm">
                    {{ variant.isValid ? "valid" : "invalid" }} (HTTP {{ variant.status }})
                  </td>
                  <td class="font-size-sm">
                    <div
                      v-for="(line, index) in variant.errors"
                      :key="variant.label + index"
                    >
                      {{ line }}
                    </div>
                    <span v-if="!variant.errors.length">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              class="font-size-sm"
              :class="debtorProbeResult.anyKept ? 'text-success' : 'text-danger'"
            >
              {{ debtorProbeResult.conclusion }}
            </div>
            <div v-if="debtorProbeResult.aggregateError" class="font-size-sm text-danger">
              {{ debtorProbeResult.aggregateError }}
            </div>
          </template>
        </div>

        <div v-if="comparison.isPosted" class="text-danger font-size-sm">
          This INSIGHT transaction is already posted — it can never be reposted.
        </div>
        <div v-else-if="!comparison.matches" class="font-size-sm text-muted">
          Correct transaction {{ comparison.transactionId }} in INSIGHT so it matches the reviewed
          figures above, then Re-check and use Create draft to adopt it.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.posting-rail__steps > li.is-done {
  opacity: 0.85;
}
.posting-rail__steps > li.is-active .font-weight-bold {
  color: #1bc5bd;
}
.posting-rail__steps > li.is-blocked {
  opacity: 0.55;
}
</style>

<script>
import {
  createDraft,
  readbackInspect,
  debtorProbe,
  generateSchedule,
  downloadGeneratedSchedule,
  attachSchedule,
  postingStatus
} from "@/services/insight-post-service";
import {
  POSTING_REFUSAL_RENEWAL_ORPHAN_PERIOD,
  REFUSAL_RECOVERY_ACKNOWLEDGEMENT_LABEL
} from "@/copy/posting-copy";

export default {
  name: "PostingRail",

  props: {
    ledgerRowId: {
      type: String,
      required: true
    },
    trustsPass: {
      type: Boolean,
      default: false
    },
    /** Review model assemblyRefusalCode === renewal_orphan_period — show recovery UX before create-draft. */
    recoverableAssemblyRefusal: {
      type: Boolean,
      default: false
    },
    /** Insurer-change (or other Create-lane) next step from review. Not a read-back compare. */
    createLaneNextStep: {
      type: String,
      default: ""
    },
    isPosted: {
      type: Boolean,
      default: false
    },
    // Identifiers only — never money/vehicle/term figures — passed through to Desk's schedule
    // render call. Both are optional; Desk resolves what it can from ledgerRowId alone.
    policyNumber: {
      type: String,
      default: ""
    },
    expectedSnapshotHash: {
      type: String,
      default: ""
    },
    /** Parent is reloading policy-processing-readiness after draft/schedule progress. */
    readinessLoading: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      statusLoading: false,
      creating: false,
      attaching: false,
      comparing: false,
      probing: false,
      generating: false,
      status: null,
      comparison: null,
      debtorProbeResult: null,
      debtorProbeError: null,
      canCompare: false,
      railError: null,
      railInsightErrors: [],
      railInfo: null,
      pendingScheduleBase64: null,
      pendingScheduleName: null,
      recoveryAcknowledgement: "",
      recoveryFieldFromRefusal: false,
      // Generate-schedule (Desk) state.
      scheduleGeneration: null,
      scheduleRefusals: [],
      scheduleGenerationConfigError: false,
      confirmTypedPremium: "",
      showManualUpload: false,
      reuploadScheduleOpen: false,
      readinessElapsedSec: 0,
      readinessTickHandle: null
    };
  },

  beforeDestroy: function() {
    this.stopReadinessTick();
  },

  computed: {
    showPostReadinessWait: function() {
      return !this.isPosted && !!this.scheduleAttached && !!this.readinessLoading;
    },
    busy: function() {
      return (
        this.statusLoading ||
        this.creating ||
        this.attaching ||
        this.comparing ||
        this.probing ||
        this.generating
      );
    },
    showRecoveryField: function() {
      return this.recoverableAssemblyRefusal || this.recoveryFieldFromRefusal;
    },
    recoveryFieldLabel: function() {
      return REFUSAL_RECOVERY_ACKNOWLEDGEMENT_LABEL;
    },
    recoveryFieldHelp: function() {
      return POSTING_REFUSAL_RENEWAL_ORPHAN_PERIOD;
    },
    /** Trusts clear, or orphan recovery with a non-empty acknowledgement token. */
    canCreateDraft: function() {
      if (this.trustsPass) {
        return true;
      }
      if (
        this.recoverableAssemblyRefusal &&
        String(this.recoveryAcknowledgement || "").trim()
      ) {
        return true;
      }
      return false;
    },
    // The preview is the pre-attach self-check: the operator must type back the SAME total the
    // preview shows (design doc §8) — this is a fat-finger/misread catch, not a cross-check against
    // another system's figure. No known total means nothing safe to check against, so confirm stays
    // disarmed rather than silently skipping the check.
    schedulePreview: function() {
      const preview =
        this.scheduleGeneration && this.scheduleGeneration.preview
          ? this.scheduleGeneration.preview
          : {};
      return {
        totalPremium:
          preview.totalPremium === undefined || preview.totalPremium === null
            ? null
            : preview.totalPremium,
        vehicleCount:
          preview.vehicleCount === undefined || preview.vehicleCount === null
            ? null
            : preview.vehicleCount,
        snapshotHash: preview.snapshotHash || null,
        docxSha256:
          preview.docxSha256 ||
          (this.scheduleGeneration && this.scheduleGeneration.sha256) ||
          null
      };
    },
    schedulePreviewTermBlocks: function() {
      const preview =
        this.scheduleGeneration && this.scheduleGeneration.preview
          ? this.scheduleGeneration.preview
          : null;
      return preview && Array.isArray(preview.termBlocks) ? preview.termBlocks : [];
    },
    scheduleDocxHashShort: function() {
      const hash =
        (this.schedulePreview && this.schedulePreview.docxSha256) ||
        (this.scheduleGeneration && this.scheduleGeneration.sha256) ||
        "";
      if (!hash) return null;
      return String(hash).slice(0, 12);
    },
    scheduleDocxFileName: function() {
      const title =
        (this.scheduleGeneration && this.scheduleGeneration.documentTitle) || "schedule";
      const base = String(title).replace(/\.docx$/i, "");
      const short = this.scheduleDocxHashShort;
      return short ? base + "-" + short + ".docx" : base + ".docx";
    },
    scheduleDocxSizeLabel: function() {
      let bytes = 0;
      if (
        this.scheduleGeneration &&
        Number.isFinite(Number(this.scheduleGeneration.contentBytes))
      ) {
        bytes = Number(this.scheduleGeneration.contentBytes);
      } else {
        const b64 =
          this.scheduleGeneration && typeof this.scheduleGeneration.contentBase64 === "string"
            ? this.scheduleGeneration.contentBase64
            : "";
        if (!b64) return "";
        bytes = Math.floor((b64.length * 3) / 4);
      }
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    confirmTypedPremiumMatches: function() {
      const expected = this.schedulePreview.totalPremium;
      if (expected === null || expected === undefined) return false;
      const typed = this.parseMoneyInput(this.confirmTypedPremium);
      return typed !== null && Math.abs(typed - Number(expected)) < 0.005;
    },
    pdfPreviewSrc: function() {
      if (!this.scheduleGeneration || !this.scheduleGeneration.pdfBase64) return "";
      return "data:application/pdf;base64," + this.scheduleGeneration.pdfBase64;
    },
    mismatchedRows: function() {
      const rows =
        this.comparison && Array.isArray(this.comparison.comparisons)
          ? this.comparison.comparisons
          : [];
      return rows.filter(row => row && row.match !== true);
    },
    // The AR allocation is the pre-post contract: INSIGHT derives the invoice debtor from it when
    // the transaction is posted, so this table is what an operator has to be able to trust.
    arAllocation: function() {
      const allocation = this.comparison && this.comparison.arAllocation;
      if (!allocation) return null;
      const money = function(row, field) {
        const entry = (row.money || []).filter(item => item.field === field)[0];
        return entry || { expected: null, actual: null, match: false };
      };
      const rows = (allocation.rows || []).map(row => {
        const commission = money(row, "Commission");
        const fee = money(row, "Fee");
        return {
          position: row.position === null ? row.label : row.position,
          label: row.label,
          expectedAuthorisedRepId: row.expectedAuthorisedRepId,
          actualAuthorisedRepId: row.actualAuthorisedRepId,
          identityMatches: row.authorisedRepMatches === true,
          expectedCommission: commission.expected,
          actualCommission: commission.actual,
          commissionMatches: commission.match === true,
          expectedFee: fee.expected,
          actualFee: fee.actual,
          feeMatches: fee.match === true
        };
      });
      let summary;
      if (allocation.matches) {
        summary =
          "The authorised reps, their order and their commission and fee figures all match the " +
          "reviewed draft, so INSIGHT will derive debtor " +
          (allocation.expectedProducingBrokerId === null
            ? "the producing broker"
            : allocation.expectedProducingBrokerId) +
          " when this is posted.";
      } else if (!allocation.participantsMatch) {
        summary =
          "INSIGHT does not hold the authorised reps the reviewed draft expects — this cannot be " +
          "posted until the allocation is corrected in INSIGHT.";
      } else if (!allocation.orderMatches) {
        summary =
          "The right authorised reps are present but in a different order. AR1 is the line the " +
          "invoice debtor comes from, so order has to be corrected before posting.";
      } else {
        summary =
          "The authorised reps and their order match, but the commission or fee figures differ " +
          "from the reviewed draft.";
      }
      return {
        matches: allocation.matches === true,
        summary: summary,
        positionNote: allocation.note,
        rows: rows
      };
    },
    debtorCandidateRows: function() {
      const candidates =
        this.comparison && this.comparison.debtorCandidates
          ? this.comparison.debtorCandidates
          : null;
      if (!candidates) return [];
      return Object.keys(candidates).map(key => ({
        key: key,
        value: candidates[key]
      }));
    },
    hasDraft: function() {
      if (this.isPosted) return true;
      if (this.transactionId == null) return false;
      const st = this.status && String(this.status.status || "").toLowerCase();
      if (!st || st === "none" || st === "failed" || st === "post_outcome_unknown") {
        return false;
      }
      return true;
    },
    scheduleAttached: function() {
      return !!(this.status && this.status.scheduleAttached);
    },
    transactionId: function() {
      return this.status && this.status.transactionId != null
        ? this.status.transactionId
        : null;
    }
  },

  mounted: function() {
    this.refreshStatus();
  },

  watch: {
    ledgerRowId: function() {
      this.refreshStatus();
    },
    showPostReadinessWait: {
      immediate: true,
      handler: function(active) {
        if (active) {
          this.startReadinessTick();
        } else {
          this.stopReadinessTick();
        }
      }
    }
  },

  methods: {
    startReadinessTick: function() {
      this.stopReadinessTick();
      this.readinessElapsedSec = 0;
      const startedAt = Date.now();
      this.readinessTickHandle = setInterval(() => {
        this.readinessElapsedSec = Math.max(
          0,
          Math.floor((Date.now() - startedAt) / 1000)
        );
      }, 250);
    },

    stopReadinessTick: function() {
      if (this.readinessTickHandle) {
        clearInterval(this.readinessTickHandle);
        this.readinessTickHandle = null;
      }
      this.readinessElapsedSec = 0;
    },

    stepClass: function(step) {
      if (this.isPosted) return "is-done";
      if (step === "draft") {
        return this.hasDraft ? "is-done" : "is-active";
      }
      if (step === "schedule") {
        if (!this.hasDraft) return "is-blocked";
        if (this.reuploadScheduleOpen) return "is-active";
        return this.scheduleAttached ? "is-done" : "is-active";
      }
      if (step === "post") {
        if (!this.scheduleAttached || this.reuploadScheduleOpen) return "is-blocked";
        return "is-active";
      }
      return "";
    },

    emitReady: function() {
      const scheduleReady = this.scheduleAttached && !this.reuploadScheduleOpen;
      this.$emit("status-changed", {
        status: this.status,
        hasDraft: this.hasDraft,
        // Soft-block Post while a re-upload is in progress; durable status stays attached.
        scheduleAttached: scheduleReady,
        transactionId: this.transactionId,
        readyToPost: this.hasDraft && scheduleReady && !this.isPosted
      });
    },

    refreshStatus: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id) return;
      this.statusLoading = true;
      this.railError = null;
      return postingStatus(id)
        .then(result => {
          this.status = result || { status: "none", scheduleAttached: false };
          this.statusLoading = false;
          this.emitReady();
          return this.status;
        })
        .catch(() => {
          this.statusLoading = false;
          this.railError = "Unable to read posting status for this billing line.";
          this.emitReady();
          return null;
        });
    },

    displayValue: function(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (value === true) return "yes";
      if (value === false) return "no";
      return String(value);
    },

    // Entity records are reported verbatim: the exact fields INSIGHT returned, unedited.
    asJson: function(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (typeof value !== "object") return String(value);
      try {
        return JSON.stringify(value);
      } catch (e) {
        return String(value);
      }
    },

    loadComparison: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.comparing) return;
      this.comparing = true;
      this.comparison = null;
      return readbackInspect(
        id,
        String(this.recoveryAcknowledgement || "").trim() || undefined
      )
        .then(result => {
          this.comparing = false;
          if (!result || result.ok === false) {
            this.railError =
              (result && (result.error || result.detail)) ||
              "Unable to compare this transaction with INSIGHT.";
            return null;
          }
          this.comparison = result;
          return result;
        })
        .catch(() => {
          this.comparing = false;
          this.railError = "Unable to compare this transaction with INSIGHT.";
          return null;
        });
    },

    loadDebtorProbe: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.probing) return;
      this.probing = true;
      this.debtorProbeError = null;
      this.debtorProbeResult = null;
      return debtorProbe(
        id,
        String(this.recoveryAcknowledgement || "").trim() || undefined
      )
        .then(result => {
          this.probing = false;
          if (!result || result.ok === false) {
            this.debtorProbeError =
              (result && (result.error || result.detail)) ||
              "Unable to probe the INSIGHT debtor placement.";
            return null;
          }
          this.debtorProbeResult = result;
          return result;
        })
        .catch(() => {
          this.probing = false;
          this.debtorProbeError =
            "Unable to probe the INSIGHT debtor placement right now.";
          return null;
        });
    },

    insightErrorsOf: function(result) {
      const fields = result && result.fields;
      return fields && Array.isArray(fields.insightErrors)
        ? fields.insightErrors.filter(line => !!line)
        : [];
    },

    onCreateDraft: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.busy || !this.canCreateDraft) return;
      if (
        this.recoverableAssemblyRefusal &&
        !String(this.recoveryAcknowledgement || "").trim()
      ) {
        this.railError =
          "Type the Recovery acknowledgement exactly, then press Create draft.";
        return;
      }
      this.creating = true;
      this.railError = null;
      this.railInsightErrors = [];
      this.railInfo = null;
      const body = {};
      const ack = String(this.recoveryAcknowledgement || "").trim();
      if (ack) {
        body.recoveryAcknowledgement = ack;
      }
      createDraft(id, body)
        .then(result => {
          this.creating = false;
          if (!result || result.ok === false) {
            const msg =
              (result && (result.error || result.detail || result.message)) ||
              "Create draft was refused.";
            this.railError = msg;
            this.railInsightErrors = this.insightErrorsOf(result);
            const code = result && result.code;
            if (
              code === "renewal_orphan_period" ||
              /acknowledgement|orphan|recovery/i.test(String(msg))
            ) {
              this.recoveryFieldFromRefusal = true;
            }
            if (/existing INSIGHT renewal \d+/i.test(String(msg))
              || /an existing INSIGHT renewal/i.test(String(msg))) {
              this.canCompare = true;
              return this.loadComparison();
            }
            return;
          }
          this.canCompare = false;
          this.comparison = null;
          this.railInfo =
            result.transactionId != null
              ? "Draft ready — INSIGHT transaction " + result.transactionId + "."
              : "Draft ready.";
          return this.refreshStatus();
        })
        .catch(() => {
          this.creating = false;
          this.railError = "Unable to create the INSIGHT draft right now.";
        });
    },

    displayMoney: function(value) {
      if (value === null || value === undefined || value === "") return "—";
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      return "$" + n.toFixed(2);
    },

    parseMoneyInput: function(text) {
      if (text === null || text === undefined) return null;
      const cleaned = String(text).replace(/[,$\s]/g, "");
      if (!cleaned) return null;
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : null;
    },

    onGenerateSchedule: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.busy) return;
      this.generating = true;
      this.railError = null;
      this.railInfo = "Generating schedule from the bound snapshot…";
      this.scheduleRefusals = [];
      this.scheduleGenerationConfigError = false;
      this.scheduleGeneration = null;
      this.confirmTypedPremium = "";
      const identifiers = {};
      if (this.policyNumber) identifiers.policyNumber = this.policyNumber;
      if (this.expectedSnapshotHash) {
        identifiers.expectedSnapshotHash = this.expectedSnapshotHash;
      }
      generateSchedule(id, identifiers)
        .then(result => {
          this.generating = false;
          if (!result || result.ok === false) {
            const message =
              (result && (result.error || result.detail || result.message)) ||
              "Unable to generate the schedule.";
            this.scheduleRefusals =
              result && Array.isArray(result.refusals) && result.refusals.length
                ? result.refusals
                : [message];
            this.scheduleGenerationConfigError = !!(
              result && result.code === "desk_not_configured"
            );
            this.railError = message;
            this.railInfo = null;
            return null;
          }
          this.scheduleGeneration = result;
          const hash =
            (result.preview && result.preview.snapshotHash) ||
            result.sha256 ||
            "";
          this.railInfo = hash
            ? "Schedule ready — downloading Word file. Snapshot " +
              String(hash).slice(0, 12) +
              "…"
            : "Schedule ready — downloading Word file.";
          // Always push the DOCX to the operator — preview metadata alone is easy to miss.
          this.downloadPendingDocx();
          return result;
        })
        .catch(err => {
          this.generating = false;
          const detail =
            (err && err.message) || "Unable to reach the schedule generator right now.";
          this.scheduleRefusals = [detail];
          this.railError = detail;
          this.railInfo = null;
        });
    },

    downloadPendingDocx: function() {
      if (!this.scheduleGeneration) return;

      // Preferred: authenticated download by renderToken (generate no longer embeds base64).
      if (this.scheduleGeneration.renderToken && this.ledgerRowId) {
        const fileName = this.scheduleDocxFileName;
        downloadGeneratedSchedule(
          String(this.ledgerRowId).trim(),
          this.scheduleGeneration.renderToken
        )
          .then(blob => {
            if (!blob) return;
            // If the server returned a JSON error as blob, surface it.
            if (blob.type && blob.type.indexOf("application/json") !== -1) {
              return blob.text().then(text => {
                let message = "Unable to download the generated schedule.";
                try {
                  const parsed = JSON.parse(text);
                  message =
                    (parsed && parsed.result && (parsed.result.error || parsed.result.message)) ||
                    (parsed && parsed.error) ||
                    message;
                } catch (e) {
                  /* keep default */
                }
                this.railError = message;
              });
            }
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(() => {
            this.railError =
              "Schedule was generated but the Word download failed — click Download schedule again.";
          });
        return;
      }

      // Legacy fallback when contentBase64 is still on the generate payload.
      if (!this.scheduleGeneration.contentBase64) return;
      const byteChars = atob(this.scheduleGeneration.contentBase64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        bytes[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = this.scheduleDocxFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    openReuploadSchedule: function() {
      this.reuploadScheduleOpen = true;
      this.railError = null;
      this.railInfo = null;
      this.scheduleGeneration = null;
      this.scheduleRefusals = [];
      this.confirmTypedPremium = "";
      this.pendingScheduleBase64 = null;
      this.pendingScheduleName = null;
      this.showManualUpload = false;
      this.emitReady();
    },

    cancelReuploadSchedule: function() {
      this.reuploadScheduleOpen = false;
      this.scheduleGeneration = null;
      this.scheduleRefusals = [];
      this.confirmTypedPremium = "";
      this.pendingScheduleBase64 = null;
      this.pendingScheduleName = null;
      this.showManualUpload = false;
      if (this.$refs.scheduleInput) {
        this.$refs.scheduleInput.value = "";
      }
      this.emitReady();
    },

    onAttachGeneratedSchedule: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || !this.scheduleGeneration || !this.confirmTypedPremiumMatches || this.busy) {
        return;
      }
      const replacing = !!this.scheduleAttached;
      this.attaching = true;
      this.railError = null;
      this.railInfo = null;
      attachSchedule(id, {
        renderToken: this.scheduleGeneration.renderToken,
        contentBase64: this.scheduleGeneration.contentBase64,
        sha256: this.scheduleGeneration.sha256
      })
        .then(result => {
          this.attaching = false;
          if (!result || result.ok === false) {
            this.railError =
              (result && (result.error || result.detail || result.message)) ||
              "Attach schedule was refused.";
            return;
          }
          this.railInfo = replacing
            ? "Replacement schedule uploaded to the draft."
            : "Generated schedule attached.";
          this.scheduleGeneration = null;
          this.scheduleRefusals = [];
          this.confirmTypedPremium = "";
          this.reuploadScheduleOpen = false;
          return this.refreshStatus();
        })
        .catch(() => {
          this.attaching = false;
          this.railError = "Unable to attach the schedule right now.";
        });
    },

    onScheduleFile: function(event) {
      const file =
        event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;
      this.pendingScheduleBase64 = null;
      this.pendingScheduleName = null;
      this.railError = null;
      if (!file) return;
      const name = String(file.name || "").toLowerCase();
      if (!name.endsWith(".docx")) {
        this.railError = "Schedule must be a .docx Word document.";
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const comma = result.indexOf(",");
        const b64 = comma >= 0 ? result.slice(comma + 1) : result;
        if (!b64) {
          this.railError = "Could not read that schedule file.";
          return;
        }
        this.pendingScheduleBase64 = b64;
        this.pendingScheduleName = file.name;
      };
      reader.onerror = () => {
        this.railError = "Could not read that schedule file.";
      };
      reader.readAsDataURL(file);
    },

    onAttachSchedule: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || !this.pendingScheduleBase64 || this.busy) return;
      const replacing = !!this.scheduleAttached;
      this.attaching = true;
      this.railError = null;
      this.railInfo = null;
      attachSchedule(id, this.pendingScheduleBase64)
        .then(result => {
          this.attaching = false;
          if (!result || result.ok === false) {
            this.railError =
              (result && (result.error || result.detail || result.message)) ||
              "Attach schedule was refused.";
            return;
          }
          if (replacing) {
            this.railInfo = this.pendingScheduleName
              ? "Re-uploaded " + this.pendingScheduleName + " to the draft."
              : "Replacement schedule uploaded to the draft.";
          } else {
            this.railInfo = this.pendingScheduleName
              ? "Attached " + this.pendingScheduleName + "."
              : "Schedule attached.";
          }
          this.pendingScheduleBase64 = null;
          this.pendingScheduleName = null;
          this.reuploadScheduleOpen = false;
          if (this.$refs.scheduleInput) {
            this.$refs.scheduleInput.value = "";
          }
          return this.refreshStatus();
        })
        .catch(() => {
          this.attaching = false;
          this.railError = "Unable to attach the schedule right now.";
        });
    }
  }
};
</script>
